import { flow, types } from 'mobx-state-tree';
import { onSnapshot } from 'mobx-state-tree';
import { axiosRequest, CustomErrorModel } from 'shared/api';
import { ITimeslot, TimeslotMonth } from './types';
import { bookingStore } from 'entities/booking';
import { addMonths, format } from 'date-fns';
import { formatWithLocale } from 'shared/lib/date';
import { doctorsStore } from 'features/doctors';

const TimeslotMonthModel = types.model({
	date: types.string,
	has_slots: types.boolean,
});

const DoctorSlotModel = types.model({
	id: types.number,
	start: types.string,
	end: types.string,
	duration: types.number,
	staff: types.number,
	branch: types.number,
});

const TimeslotMapModel = types.map(types.map(types.array(DoctorSlotModel)));
const SlotsByDateMapModel = types.map(types.array(types.number));
const MonthSlotsMapModel = types.map(types.boolean);

export const TimeslotsStore = types
	.model('TimeslotsStore', {
		monthSlots: types.array(TimeslotMonthModel),
		monthSlotsMap: types.optional(MonthSlotsMapModel, {}),
		doctorsSlots: types.optional(TimeslotMapModel, {}),
		slotsByDatesMap: types.optional(SlotsByDateMapModel, {}),
		errorMonthsSlots: types.maybeNull(CustomErrorModel),
		errorDoctorSlots: types.maybeNull(CustomErrorModel),
		loadingMonthsSlots: types.boolean,
	})
	.actions(self => ({
		fetchMonths: flow(function* (id?: string | number) {
      console.log('id', id)
			try {
				self.errorMonthsSlots = null;
				self.loadingMonthsSlots = true;

				const branch = bookingStore.clinics.map(clinic => clinic.id);
				const spec = bookingStore.specialization?.id;
				const currentDate = new Date();
				const date_from = format(currentDate, 'yyyy-MM-dd');
				const date_to = format(addMonths(currentDate, 2), 'yyyy-MM-dd');

        type TParams = {
          spec: any;
          date_from: string;
          date_to: string;
          all: boolean;
          branch: number[];
          staff?: string | number
        }

        const params: TParams = {
          spec, date_from, date_to, all: true, branch
        }

        if (id) {
          params.staff = id
        }

				if (spec) {
					const timeslots: TimeslotMonth[] = yield axiosRequest({
						path: '/api/v2/booking/timeslot/month',
						params,
					});

					if (timeslots) {
						const monthSlotsMap: Record<string, boolean> = {};
						timeslots.map(timeslot => {
							monthSlotsMap[timeslot.date] = timeslot.has_slots;
						});

						if (bookingStore.date) {
							const slotIsExist =
								monthSlotsMap[bookingStore.date];
							if (!slotIsExist) {
								bookingStore.removeDate();
							}
						}

						self.monthSlotsMap.replace(monthSlotsMap);
						self.monthSlots.replace(timeslots);
						self.loadingMonthsSlots = false;
						return timeslots;
					}
				}
			} catch (error) {
				console.error('Error fetching slots:', error);
				self.loadingMonthsSlots = false;
			}
		}),
		fetchSlotsByDoctors: flow(function* (date: string, id?: string | number) {
			try {
				self.errorDoctorSlots = null;
				const spec = bookingStore.specialization?.id || doctorsStore.activeDoctor?.specializations[0]?.id;
				const branch = bookingStore.clinics.map(clinic => clinic.id);
				if (!date) date = bookingStore.date;

        type TParams = {spec: any; date: string; branch: number[];staff?:string | number};

        const params: TParams = {
          spec, 
          date, 
          branch
        }

        if (id) {
          params.staff = id;
        }

				const timeslots: ITimeslot[] = yield axiosRequest({
					path: '/api/v2/booking/timeslot',
					params,
				});

				if (!timeslots) {
					throw new Error('error');
				}

				if (!timeslots.length) {
					self.errorDoctorSlots = {
						message: '',
						code: 'NOT_FOUND',
						status: 404,
					};
					return;
				}

				// Используем вложенную карту: сначала по staff, потом по branch
				const staffBranchMap: Record<
					number,
					Record<number, ITimeslot[]>
				> = {}; // staff -> branch -> timeslots
				const slotsByDateMap: Record<string, number[]> = {}; // Для группировки по датам

        const timeslotsFiltered = timeslots.filter(timeslot => new Date(timeslot.end) > new Date());
				timeslotsFiltered.forEach(timeslot => {
					const { staff, branch, start } = timeslot;

					// Группировка по врачам и клиникам
					if (!staffBranchMap[staff]) {
						staffBranchMap[staff] = {}; // Если еще нет, инициализируем
					}

					if (!staffBranchMap[staff][branch]) {
						staffBranchMap[staff][branch] = []; // Если еще нет клиники для этого врача, инициализируем
					}

					staffBranchMap[staff][branch].push(timeslot);

					// Группировка по датам
					const formattedDate = formatWithLocale(start, 'dd.MM.yyyy');
					if (!slotsByDateMap[formattedDate]) {
						slotsByDateMap[formattedDate] = [];
					}
					if (!slotsByDateMap[formattedDate].includes(branch)) {
						slotsByDateMap[formattedDate].push(branch);
					}
				});

				// Обновляем состояние с вложенной картой врачей и клиник
				self.doctorsSlots.replace(staffBranchMap);
				self.slotsByDatesMap.replace(slotsByDateMap);
			} catch (error) {
				console.error('Error fetching doctor slots:', error);
				// self.errorDoctorSlots = error as any;
			}
		}),
	}));

export const timeslotsStore = TimeslotsStore.create({
	monthSlots: [],
	doctorsSlots: {},
	slotsByDatesMap: {},
	monthSlotsMap: {},
	errorMonthsSlots: null,
	errorDoctorSlots: null,
	loadingMonthsSlots: false,
});

//Если нужно дебажить
onSnapshot(timeslotsStore, snapshot => {
  if (import.meta.env.VITE_SHOW_SNAPSHOT !== 'false') {
    console.log('Таймслоты снимок состояния:', snapshot);
  }
});

// onAction(documentsStore, action => {
// 	console.log("Экшен", action);
// })
